import React, { useState, useMemo, useRef, useEffect } from 'react';
// Using regular CSS classes instead of CSS modules for shadow DOM compatibility
import Header from '../shared/Header.jsx';
import SessionCard from './SessionCard.jsx';
import SessionModal from '../shared/SessionModal.jsx';
import SpeakerModal from '../shared/SpeakerModal.jsx';
import SearchFilters from '../shared/SearchFilters.jsx';
import { MODAL_TYPES, WIDGET_TYPES } from '../constants.js';
import { useModalStack } from '../hooks/useModalStack.js';
import {
  useOpenRecordFromUrl,
  useHighlightedSessionIds,
  URL_RECORD_PARAMS,
} from '../hooks/useOpenRecordFromUrl.js';
import { usePagination } from '../hooks/usePaginationControls.js';
import { useAddToCalendarScript } from '../hooks/useAddToCalendarScript.js';
import {
  firstDatedHighlight,
  scheduleScrollSessionIntoView,
} from '../utils/scroll-session-into-view.js';

import { ITEMS_PER_PAGE } from '../constants.js';
import { getAvailableFilters, advancedFilterSessions } from '../utils/filters.js';
import { buildSessionSearchFields } from '../utils/search.js';
import { getSessionDataForModal, getSpeakerDataForModal } from './utils.js';
import getVisibleFieldIdsOfType, { getVisibleFieldsOfTypeById } from '../utils/get-visible-fields-of-widget.js';

import Footer from '../shared/Footer.jsx';
import NoResults from '../shared/NoResults.jsx';
import PaginationControls from '../shared/PaginationControls.jsx';
import Loading from '../shared/Loading.jsx';
import SubsessionModal from '../shared/SubsessionModal.jsx';
import { getSubsessionDataForModal } from '../utils/subsession-modal.js';
import { useI18n } from '../i18n/index.jsx';

/**
 * @param {import('../types/async-data.js').AsyncDataResponse<import('../types/async-data.js').SessionScopeData>} data
 * @param {boolean} loading
 */
const SessionsWidget = ({ data = null, loading = false }) => {
  const { t } = useI18n();
  const { sessions: allSessions = [], sessionsById = {}, speakersById = {}, filterOptions = {} } = data?.data || {};
  const { embedFields = [], groupedFields = {}, primaryColor } = data || {};
  const showAddToCalendar = filterOptions?.show_add_to_calendar;
  const showSessionSponsors = filterOptions?.show_session_sponsors ?? false;
  const useModals = data?.useModals || false;
  const isDarkTheme = data?.isDarkTheme || false;
  const { isLoaded: isAddToCalendarScriptLoaded } = useAddToCalendarScript(showAddToCalendar);

  const visibleSpeakerFields = getVisibleFieldIdsOfType(groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSpeakerFieldsById = getVisibleFieldsOfTypeById(groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSessionFields = getVisibleFieldIdsOfType(groupedFields, WIDGET_TYPES.SESSIONS);
  const visibleSessionFieldsById = getVisibleFieldsOfTypeById(groupedFields, WIDGET_TYPES.SESSIONS);

  // Refs for DOM elements
  const scrollWrapperRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  // Advanced filter and search logic matching legacy widget
  const availableFilters = useMemo(() => {
    return getAvailableFilters(filterOptions, embedFields);
  }, [filterOptions, embedFields]);

  const filteredSessions = useMemo(() => {
    const searchFields = buildSessionSearchFields(availableFilters, visibleSessionFields);
    return advancedFilterSessions(allSessions, searchQuery, activeFilters, availableFilters, {
      searchFields,
      fieldMappingStyle: 'id', //  SessionsWidget uses formatId, trackId, etc.
    });
  }, [allSessions, searchQuery, activeFilters, availableFilters, visibleSessionFields]);

  const { visibleModal, openModal, closeModal } = useModalStack();

  useOpenRecordFromUrl({
    ready: !loading && useModals,
    shortcuts: [
      {
        param: URL_RECORD_PARAMS.SESSION,
        modalType: MODAL_TYPES.SESSION,
        exists: id => Boolean(sessionsById[id]),
      },
    ],
    openModal,
  });
  const highlightedSessionIds = useHighlightedSessionIds({ ready: !loading });

  const { currentPage, totalPages, handlePageChange, getPaginatedItems } = usePagination(
    filteredSessions.length,
    ITEMS_PER_PAGE,
    [searchQuery, activeFilters] // dependencies that should reset pagination
  );

  useEffect(() => {
    if (loading || !highlightedSessionIds.size) return;
    const pick = firstDatedHighlight(highlightedSessionIds, id =>
      filteredSessions.findIndex(s => s.id === id)
    );
    if (!pick) return;

    if (pick.dayIndex >= 0) {
      const page = Math.floor(pick.dayIndex / ITEMS_PER_PAGE);
      if (page !== currentPage) {
        handlePageChange(page);
        return;
      }
      scheduleScrollSessionIntoView(() => scrollWrapperRef.current, pick.id);
      return;
    }

    if (useModals && sessionsById[pick.id]) {
      openModal(MODAL_TYPES.SESSION, pick.id);
    }
  }, [
    loading,
    highlightedSessionIds,
    filteredSessions,
    currentPage,
    handlePageChange,
    useModals,
    sessionsById,
    openModal,
  ]);

  // Pagination logic
  const paginatedSessions = getPaginatedItems(filteredSessions);
  const hasActiveFilters = searchQuery.trim() || Object.values(activeFilters).some(arr => arr.length > 0);

  // Event handlers
  const handleOpenSession = sessionId => {
    openModal(MODAL_TYPES.SESSION, sessionId);
  };

  const handleSpeakerClick = speakerId => {
    openModal(MODAL_TYPES.SPEAKER, speakerId);
  };

  // SB-8856: subsession rows push a subsession detail modal on the stack.
  const handleSubsessionClick = subsessionId => {
    openModal(MODAL_TYPES.SUBSESSION, subsessionId);
  };

  const handleCloseModal = (type, id) => {
    closeModal(type, id);
  };

  const handleSearch = query => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filterType, selectedValues) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: selectedValues,
    }));
  };

  const handlePageChangeWithScroll = newPage => {
    handlePageChange(newPage);
    // Scroll to top of sessions list (matches legacy behavior)
    if (scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollTo(0, 0);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setActiveFilters({});
    handlePageChange(0);
  };

  // Handle cases where data might be null/undefined or missing structure
  if (!data && loading) {
    return (
      <div className="sessions-widget__loading">
        <Loading size="large" colorVariant="primary" text={t('loading_data')} />
      </div>
    );
  }

  return (
    <div
      className={`sessions-widget ${isDarkTheme ? 'theme-dark' : ''}`}
      style={primaryColor ? { '--color-primary': primaryColor } : {}}
    >
      <Header
        eventName={data?.eventName || t('event')}
        primaryColor={data?.primaryColor}
        hidden={Boolean(data?.hideHeader)}
      />

      {/* Pagination Controls */}
      {filteredSessions.length > 0 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredSessions.length}
          itemsPerPage={ITEMS_PER_PAGE}
          widgetType={t('pagination_sessions')}
          onPageChange={handlePageChangeWithScroll}
        />
      )}

      <SearchFilters
        searchQuery={searchQuery}
        onSearch={handleSearch}
        availableFilters={availableFilters}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        showSearch={filterOptions?.search_speaker_session_by_name}
        filteredItemsCount={filteredSessions.length}
        handleClearFilters={handleClearFilters}
      />

      <div className="sessions-widget__content">
        {loading ? (
          <div className="sessions-widget__loading">
            <div className="sessions-widget__loading-content">{t('loading_data')}</div>
          </div>
        ) : filteredSessions.length === 0 ? (
          <NoResults
            widgetItem={t('items_sessions')}
            searchQuery={searchQuery}
            hasActiveFiltersOrSearch={hasActiveFilters}
            handleClearFilters={handleClearFilters}
          />
        ) : (
          <div className="sessions-widget__session-list" ref={scrollWrapperRef}>
            {paginatedSessions.map(session => (
              <SessionCard
                sessionData={session}
                key={session.id}
                isHighlighted={highlightedSessionIds.has(session.id)}
                visibleSessionFields={visibleSessionFields}
                visibleSessionFieldsById={visibleSessionFieldsById}
                speakers={
                  session.speakers?.map(speaker => ({
                    id: speaker.id,
                    fullName: speaker.full_name,
                    role: speaker.role,
                    // SB-8853: slug + organizer labels for role grouping.
                    role_slug: speaker.role_slug || speaker.role || 'speaker',
                    role_label: speaker.role_label || null,
                    role_label_plural: speaker.role_label_plural || null,
                    salutation: speakersById[speaker.id]?.salutation || '',
                    honorific: speakersById[speaker.id]?.honorific || '',
                    title: speakersById[speaker.id]?.title || '',
                    company: speakersById[speaker.id]?.company_name || '',
                    photo: speakersById[speaker.id]?.photo || '',
                  })) || []
                }
                visibleSpeakerFields={visibleSpeakerFields}
                onSessionClick={handleOpenSession}
                onSpeakerClick={handleSpeakerClick}
                showAddToCalendar={showAddToCalendar && isAddToCalendarScriptLoaded}
                showSessionSponsors={showSessionSponsors}
                sessionForCalendar={{
                  title: session.title,
                  startsAtAddEvent: session.starts_at_addEvent,
                  endsAtAddEvent: session.ends_at_addEvent,
                  startTimeAddEvent: session.start_time_addEvent,
                  endTimeAddEvent: session.end_time_addEvent,
                  timezone: session.timezone,
                  description: session.description,
                  location: session.location,
                }}
              />
            ))}
          </div>
        )}
      </div>
      {useModals && visibleModal?.type === MODAL_TYPES.SESSION && (
        <SessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          sessionData={getSessionDataForModal(visibleModal.id, sessionsById, speakersById)}
          onClose={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          onSubsessionClick={handleSubsessionClick}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
          showAddToCalendar={showAddToCalendar && isAddToCalendarScriptLoaded}
        />
      )}
      {useModals && visibleModal?.type === MODAL_TYPES.SUBSESSION && (
        <SubsessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          subsessionData={getSubsessionDataForModal(visibleModal.id, sessionsById, speakersById)}
          onClose={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
        />
      )}
      {useModals && visibleModal?.type === MODAL_TYPES.SPEAKER && (
        <SpeakerModal
          speakerData={getSpeakerDataForModal(visibleModal.id, speakersById, sessionsById)}
          onClose={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          onSessionClick={handleOpenSession}
          visibleSpeakerFields={visibleSpeakerFields}
          visibleSpeakerFieldsById={visibleSpeakerFieldsById}
        />
      )}
      {/* Footer */}
      <Footer hidden={Boolean(data?.hideFooter)} />
    </div>
  );
};

export default SessionsWidget;
